import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, CheckCircle2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Inline button that lets the admin attach "before" or "after" photos to an item
 * without opening the item editor.
 *
 * Visual state:
 *   - Both photos set → shows "Fotos OK" (green pill, clicking still opens the picker)
 *   - Only "before" OR "after" set → shows "Anexar foto depois" (or antes)
 *   - None → shows "Anexar fotos"
 *
 * On click, it opens an action sheet so the admin can pick which kind to upload.
 */
export default function ItemPhotoButton({ budgetId, item, onUpdate, testid }) {
  const beforeCameraRef = useRef(null);
  const beforeGalleryRef = useRef(null);
  const afterCameraRef = useRef(null);
  const afterGalleryRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [kindOpen, setKindOpen] = useState(null); // "before" | "after" | null
  const [busy, setBusy] = useState(null); // "before" | "after" | null

  const hasBefore = !!item?.before_photo_path;
  const hasAfter = !!item?.after_photo_path;
  const bothOk = hasBefore && hasAfter;

  let label = "Anexar fotos";
  if (bothOk) label = "Fotos OK";
  else if (hasBefore && !hasAfter) label = "Anexar foto depois";
  else if (!hasBefore && hasAfter) label = "Anexar foto antes";

  const openKindMenu = (kind) => { setKindOpen(kind); setOpen(false); };
  const chooseFile = (kind, source) => {
    const ref = kind === "before"
      ? (source === "camera" ? beforeCameraRef : beforeGalleryRef)
      : (source === "camera" ? afterCameraRef : afterGalleryRef);
    ref.current?.click();
    setKindOpen(null);
  };

  const upload = async (kind, file) => {
    if (!file) return;
    setBusy(kind);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const path = r.data?.path;
      if (!path) throw new Error("Upload falhou");
      const resp = await api.patch(`/budgets/${budgetId}/items/${item.id}/photo`, { kind, path });
      toast.success(`Foto ${kind === "before" ? "ANTES" : "DEPOIS"} anexada`);
      onUpdate?.(resp.data?.item);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao enviar foto");
    } finally {
      setBusy(null);
      setOpen(false);
      setKindOpen(null);
    }
  };

  const btnCls = bothOk
    ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
    : "bg-violet-50 text-[#7c3aed] border-violet-100 hover:bg-violet-100";

  return (
    <div className="relative inline-block">
      <input ref={beforeCameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => upload("before", e.target.files?.[0])} data-testid={`${testid}-input-before-camera`} />
      <input ref={beforeGalleryRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload("before", e.target.files?.[0])} data-testid={`${testid}-input-before-gallery`} />
      <input ref={afterCameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => upload("after", e.target.files?.[0])} data-testid={`${testid}-input-after-camera`} />
      <input ref={afterGalleryRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload("after", e.target.files?.[0])} data-testid={`${testid}-input-after-gallery`} />
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={!!busy}
        data-testid={testid}
        className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2.5 py-1 border transition-colors ${btnCls}`}
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (bothOk ? <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2.5} /> : <Camera className="w-3.5 h-3.5" strokeWidth={2.3} />)}
        {label}
      </button>
      {open && !busy && !kindOpen && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 z-40 w-44 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden">
            <button
              className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 flex items-center gap-2"
              onClick={() => openKindMenu("before")}
              data-testid={`${testid}-choose-before`}
            >
              <Camera className="w-3.5 h-3.5 text-violet-600" />
              Foto <strong>antes</strong>{hasBefore ? " · substituir" : ""}
            </button>
            <button
              className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 flex items-center gap-2 border-t border-slate-100"
              onClick={() => openKindMenu("after")}
              data-testid={`${testid}-choose-after`}
            >
              <Camera className="w-3.5 h-3.5 text-emerald-600" />
              Foto <strong>depois</strong>{hasAfter ? " · substituir" : ""}
            </button>
          </div>
        </>
      )}
      {kindOpen && !busy && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setKindOpen(null)} />
          <div className="absolute right-0 mt-1 z-40 w-44 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden">
            <button
              className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 flex items-center gap-2"
              onClick={() => chooseFile(kindOpen, "camera")}
              data-testid={`${testid}-source-camera`}
            >
              📷 Tirar foto agora
            </button>
            <button
              className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 flex items-center gap-2 border-t border-slate-100"
              onClick={() => chooseFile(kindOpen, "gallery")}
              data-testid={`${testid}-source-gallery`}
            >
              🖼️ Escolher da galeria
            </button>
          </div>
        </>
      )}
    </div>
  );
}
